import type { ReactElement } from "react"

import { useTranslation } from "react-i18next"

import { Select } from "@synapse-analytics/synapse-ui"
import { useFormikContext } from "formik"

import { InfoBlock } from "../../../../components/containers/InfoBlock"
import type { ConfigFormikProps, SelectFieldsProps } from "../../Interfaces"

import styles from "./SelectFields.module.scss"

/**
 * Component for the positive and negative label select fields in the AutoML configuration page.
 * The component renders two material UI select components with the following properties:
 * - Positive Label: for selecting the positive label
 * - Negative Label: for selecting the negative label
 * The component also renders the error messages for the positive and negative labels.
 * @param {SelectFieldsProps} props - component props
 * @returns {ReactElement} a React element representing the select fields
 */
export function SelectFields(props: Readonly<SelectFieldsProps>): ReactElement {
  const { onChangeHandler, mode } = props
  const { t } = useTranslation("ModelConfigurations")

  const {
    values: { positiveLabel, negativeLabel, labels },
    errors,
    touched,
    handleBlur,
  } = useFormikContext<ConfigFormikProps>()

  return (
    <section className={styles.selectFieldsContainer}>
      {mode === "view" ? (
        <div>
          <InfoBlock title={t("labels.negative")} text={negativeLabel?.name ?? ""} type="default" />
          <InfoBlock title={t("labels.positive")} text={positiveLabel?.name ?? ""} type="default" />
        </div>
      ) : (
        <div>
          <Select
            hideDescription
            id="negativeLabel"
            label={t("labels.negative")}
            placeholder={t("labels.negative")}
            value={negativeLabel?.name}
            handleChange={onChangeHandler}
            handleBlur={handleBlur}
            error={touched?.negativeLabel && Boolean(errors?.negativeLabel) && errors?.negativeLabel}
            required
            fullWidth
            options={labels}
            disabled={labels.length === 0 && mode === "edit"}
          />
          <Select
            hideDescription
            id="positiveLabel"
            label={t("labels.positive")}
            placeholder={t("labels.positive")}
            value={positiveLabel?.name}
            handleChange={onChangeHandler}
            handleBlur={handleBlur}
            error={
              touched && errors && touched?.positiveLabel && Boolean(errors?.positiveLabel) && errors?.positiveLabel
            }
            required
            fullWidth
            options={labels}
            disabled={labels.length === 0 && mode === "edit"}
          />
        </div>
      )}
    </section>
  )
}
